import { useEffect, useId, useMemo, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { amount, quantity, unitLabel } from '../lib/format';
import { monogram, parseQuickQuery, resolveQuantity, toneOf, type QuickQuery } from '../lib/till';
import type { Paged, PosProduct } from '../lib/types';
import { Icon, type IconName } from './icons';
import { cx, Kbd, Modal } from './ui';
import { useT, type MessageKey } from '../i18n';

/**
 * "Type anything": one box for what the customer says.
 *
 *   tomato          → the product, ready to add (loose goods open the weigh pad)
 *   2 kg tomato     → "Add 2 kg Tomato (Loose) · ₹80.00", one Enter
 *   ₹50 onion       → the weight fifty rupees buys, worked out
 *   3 milk          → three of them
 *   98220 12210     → that customer
 *   hold, reprint…  → the till's own actions, with their keys
 *
 * The parsed quantity is shown on the best match BEFORE anything is added, so a product whose
 * name starts with a number cannot be added a hundred times by surprise.
 */

export interface QuickAction {
  id: string;
  label: string;
  hint?: string;
  shortcut?: string;
  icon: IconName;
  /** Extra words that should find this action. */
  keywords?: string;
  disabled?: boolean;
  run: () => void;
}

interface Props {
  initialQuery?: string;
  actions: QuickAction[];
  onClose: () => void;
  /** `qtyMilli` null means "the usual": one piece, or the weigh pad for a loose item. */
  onProduct: (product: PosProduct, qtyMilli: number | null, amountPaise: number | null) => void;
  onCustomerPhone: (phone: string) => void;
  onBarcode: (code: string) => void;
}

const NO_PRODUCTS: PosProduct[] = [];

interface Entry {
  id: string;
  group: string;
  icon?: IconName;
  mark?: { text: string; tone: number };
  title: string;
  sub?: string;
  right?: string;
  shortcut?: string;
  disabled?: boolean;
  run: () => void;
}

export function QuickFind({ initialQuery = '', actions, onClose, onProduct, onCustomerPhone, onBarcode }: Props) {
  const t = useT();
  const [text, setText] = useState(initialQuery);
  const [term, setTerm] = useState('');
  const [active, setActive] = useState(0);
  const listId = useId();

  const parsed: QuickQuery = useMemo(() => parseQuickQuery(text), [text]);
  const wanted = parsed.kind === 'item' ? parsed.term : '';

  useEffect(() => {
    const timer = window.setTimeout(() => setTerm(wanted.trim()), 150);
    return () => window.clearTimeout(timer);
  }, [wanted]);

  const search = useQuery<PosProduct[]>({
    queryKey: ['till', 'find', term],
    enabled: term.length >= 2,
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) => api.get<Paged<PosProduct>>('/api/catalog/products', { q: term, pageSize: 8 }, signal).then((result) => result.items),
  });
  const products = useMemo(() => (term.length >= 2 ? (search.data ?? NO_PRODUCTS) : NO_PRODUCTS), [term, search.data]);

  const entries = useMemo<Entry[]>(() => {
    const list: Entry[] = [];

    if (parsed.kind === 'phone') {
      list.push({
        id: 'phone',
        group: 'Customer',
        icon: 'user',
        title: t('find.customerPhone', { phone: `${parsed.phone.slice(0, 5)} ${parsed.phone.slice(5)}` }),
        sub: t('find.customerHint'),
        shortcut: 'Enter',
        run: () => onCustomerPhone(parsed.phone),
      });
    }
    if (parsed.kind === 'barcode') {
      list.push({ id: 'barcode', group: 'Barcode', icon: 'barcode', title: t('find.barcode', { code: parsed.code }), shortcut: 'Enter', run: () => onBarcode(parsed.code) });
    }

    if (parsed.kind === 'item') {
      const explicit = parsed.quantity !== null || parsed.amountPaise !== null;
      products.forEach((product, index) => {
        const unit = unitLabel(product.unit);
        const out = product.stockMilli <= 0;
        const resolved = explicit ? resolveQuantity(parsed, product) : null;
        const qty = resolved?.qtyMilli ?? null;
        const line = qty && qty > 0 ? Math.round((qty * product.sellingPricePaise) / 1000) : null;
        const detail = [product.localName, `₹${amount(product.sellingPricePaise)}/${unit}`, out ? t('find.outOfStock') : product.allowDecimalQty ? `${quantity(product.stockMilli)} ${unit}` : t('find.left', { qty: quantity(product.stockMilli) })]
          .filter(Boolean)
          .join(' · ');

        if (index === 0 && explicit) {
          list.push({
            id: `best-${product.id}`,
            group: 'Best match',
            icon: 'plus',
            title: resolved?.error ? product.name : t('find.add', { qty: quantity(qty ?? 0), unit: product.allowDecimalQty ? unit : '×', name: product.name }),
            sub: resolved?.error ?? detail,
            right: line !== null ? `₹${amount(line)}` : undefined,
            shortcut: resolved?.error ? undefined : 'Enter',
            disabled: Boolean(resolved?.error) || out,
            run: () => onProduct(product, qty, parsed.amountPaise),
          });
        }
        list.push({
          id: product.id,
          group: 'Products',
          mark: { text: monogram(product.name), tone: toneOf(product.categoryName) },
          title: product.name,
          sub: product.allowDecimalQty && !explicit ? `${detail} · ${t('grid.opensWeighPad')}` : detail,
          right: `₹${amount(product.sellingPricePaise)}`,
          disabled: out,
          run: () => onProduct(product, explicit && !resolved?.error ? qty : null, explicit ? parsed.amountPaise : null),
        });
      });

      const words = parsed.term.toLowerCase().split(' ').filter((word) => word.length >= 2);
      for (const action of actions) {
        const haystack = `${action.label} ${action.keywords ?? ''}`.toLowerCase();
        if (words.length > 0 && !words.some((word) => haystack.includes(word))) continue;
        list.push({ id: `action-${action.id}`, group: 'Actions', icon: action.icon, title: action.label, sub: action.hint, shortcut: action.shortcut, disabled: action.disabled, run: action.run });
      }
    } else if (parsed.kind === 'empty') {
      for (const action of actions) {
        list.push({ id: `action-${action.id}`, group: 'Actions', icon: action.icon, title: action.label, sub: action.hint, shortcut: action.shortcut, disabled: action.disabled, run: action.run });
      }
    }
    return list;
  }, [parsed, products, actions, onCustomerPhone, onBarcode, onProduct]);

  // A new query or new results put the highlight back on the first thing that can be run. Only
  // those: the till re-rendering under the palette must not yank it from where the arrows left it.
  const firstRunnable = entries.findIndex((entry) => !entry.disabled);
  useEffect(() => {
    setActive(firstRunnable === -1 ? 0 : firstRunnable);
  }, [text, products]);

  function move(delta: number) {
    if (entries.length === 0) return;
    let next = active;
    for (let step = 0; step < entries.length; step += 1) {
      next = (next + delta + entries.length) % entries.length;
      if (!entries[next]?.disabled) break;
    }
    setActive(next);
  }

  function run(entry: Entry | undefined) {
    if (!entry || entry.disabled) return;
    entry.run();
  }

  const searching = term.length >= 2 && search.isFetching;
  let lastGroup = '';

  return (
    <Modal title={t('find.title')} onClose={onClose} size="max-w-2xl" hideHeader testId="quick-find" restoreFocus="barcode" scrimClassName="items-start pt-[8vh]">
      <div className="flex h-16 items-center gap-3 border-b border-slate-200 px-4">
        <Icon name="search" size={22} className="shrink-0" style={{ color: 'var(--ui-primary)' }} />
        <input
          className="h-full min-w-0 flex-1 bg-transparent text-xl font-medium text-slate-900 outline-none placeholder:text-slate-400"
          value={text}
          data-autofocus=""
          data-testid="quick-find-input"
          placeholder={t('find.placeholder')}
          aria-label={t('find.label')}
          role="combobox"
          aria-expanded="true"
          aria-controls={listId}
          aria-activedescendant={entries[active] ? `${listId}-${active}` : undefined}
          autoComplete="off"
          spellCheck={false}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={(event) => {
            // IME composition (Tamil) commits with Enter/arrows: let it reach the field.
            const composing = event.nativeEvent.isComposing || event.keyCode === 229;
            if (composing) return;
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              move(1);
            } else if (event.key === 'ArrowUp') {
              event.preventDefault();
              move(-1);
            } else if (event.key === 'Enter') {
              event.preventDefault();
              run(entries[active]);
            }
          }}
        />
        {searching ? <span className="spinner" aria-hidden /> : null}
        <Kbd>Esc</Kbd>
      </div>

      <ul id={listId} role="listbox" aria-label={t('find.results')} className="max-h-[60vh] overflow-y-auto p-2">
        {entries.length === 0 ? (
          <li className="px-3 py-8 text-center text-sm text-slate-500">
            {term.length >= 2 && !search.isFetching ? t('find.nothing', { term }) : t('find.start')}
          </li>
        ) : null}
        {entries.map((entry, index) => {
          const header = entry.group !== lastGroup ? entry.group : null;
          lastGroup = entry.group;
          const selected = index === active;
          return (
            <li key={entry.id} role="presentation">
              {header ? <p className="px-3 pt-2.5 pb-1 text-[10.5px] font-bold tracking-[0.12em] text-slate-500 uppercase">{t(`find.group.${header}` as MessageKey)}</p> : null}
              <div
                id={`${listId}-${index}`}
                role="option"
                aria-selected={selected}
                aria-disabled={entry.disabled || undefined}
                data-testid="quick-find-result"
                className={cx(
                  'flex min-h-14 cursor-pointer items-center gap-3 rounded-xl border px-3 py-2',
                  selected ? 'border-transparent' : 'border-transparent hover:bg-slate-50',
                  entry.disabled && 'cursor-not-allowed opacity-55',
                )}
                style={selected ? { background: 'var(--ui-primary-soft)', borderColor: 'var(--ui-primary)' } : undefined}
                onMouseEnter={() => !entry.disabled && setActive(index)}
                onClick={() => run(entry)}
              >
                {entry.mark ? (
                  <span aria-hidden className={cx('mono-badge h-9 w-9 text-xs', `tone-${entry.mark.tone}`)}>
                    {entry.mark.text}
                  </span>
                ) : (
                  <span
                    aria-hidden
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[0.6rem]"
                    style={entry.group === 'Best match' ? { background: 'var(--ui-primary)', color: 'var(--ui-primary-ink)' } : { background: 'var(--ui-surface-2)', color: 'var(--ui-muted)' }}
                  >
                    <Icon name={entry.icon ?? 'search'} size={18} strokeWidth={2} />
                  </span>
                )}
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[0.95rem] font-semibold text-slate-900">{entry.title}</span>
                  {entry.sub ? (
                    <span className="block truncate text-xs" style={{ color: entry.disabled && entry.group === 'Best match' ? 'var(--ui-danger)' : 'var(--ui-faint)' }}>
                      {entry.sub}
                    </span>
                  ) : null}
                </span>
                {entry.right ? <span className="tabular shrink-0 text-[0.95rem] font-bold text-slate-900">{entry.right}</span> : null}
                {entry.shortcut ? <Kbd className="shrink-0">{entry.shortcut}</Kbd> : null}
              </div>
            </li>
          );
        })}
      </ul>

      <p className="flex flex-wrap items-center gap-x-4 gap-y-1 border-t border-slate-200 bg-slate-50 px-4 py-2.5 text-xs text-slate-500">
        <span>
          <Kbd>↑</Kbd> <Kbd>↓</Kbd> {t('find.move')}
        </span>
        <span>
          <Kbd>Enter</Kbd> {t('find.addOrRun')}
        </span>
        <span className="ml-auto">{t('find.try')}</span>
      </p>
    </Modal>
  );
}
